/**
 * A TICKET AS THE PRINTER'S OWN COMMANDS.
 *
 * The head is driven by its ROM fonts (12×24 "A", 9×17 "B"), magnified 1–8× in each direction by
 * GS !, with bold, letter tracking, alignment and tab-stop columns — the same machinery the
 * original ticket used. A ticket is ~1 KB of this, against ~66 KB as a picture, and the glyphs are
 * drawn dot-for-dot for this head, so they come out crisp; a browser-rendered picture thresholded to
 * 1 bit does not (✅ FIELD, EP802 2026-10-07: picture tickets were slow and ragged).
 *
 * A layout is a list of physical lines. Wrapping and fitting are decided by whoever composes it
 * (with `cellWidth` below), so what the composer measured is exactly what prints.
 */
import * as C from './commands';

export type Font = 'A' | 'B';
export const CELL: Record<Font, { w: number; h: number }> = { A: { w: 12, h: 24 }, B: { w: 9, h: 17 } };

export type LayoutOp =
  /** One printed line. `\t` jumps to the next of `tabs` (character columns at this line's size). */
  | { k: 'text'; text: string; font?: Font; w?: number; h?: number; bold?: boolean; align?: 'L' | 'C' | 'R'; track?: number; tabs?: number[]; lead?: number }
  /** A solid rule across the print width, `dots` tall (≥ 3 holds up on thermal paper). `via: 'underline'`
   *  draws it with the font's 2-dot underline over spaces — for firmware that prints no pictures. */
  | { k: 'rule'; dots: number; inset?: number; via?: 'raster' | 'underline' }
  /** An image stored in the printer (FS q), printed by number — 4 bytes. `heightDots` for layout. */
  | { k: 'nv'; n: number; heightDots: number }
  /** Bytes sent as they are, with the paper they take — for test slips only. */
  | { k: 'raw'; bytes: Uint8Array; heightDots: number }
  | { k: 'gap'; dots: number }
  | { k: 'qr'; data: string; module: number };

/** Dots one character advances: the cell plus its tracking, both magnified with the width. */
export const cellWidth = (font: Font = 'A', w = 1, track = 0) => (CELL[font].w + track) * w;
export const columns = (widthDots: number, font: Font = 'A', w = 1, track = 0) => Math.floor(widthDots / cellWidth(font, w, track));
/** Dots of paper a text line takes. */
export const lineHeight = (op: { font?: Font; h?: number; lead?: number }) => CELL[op.font ?? 'A'].h * (op.h ?? 1) + (op.lead ?? 6);

const CP1252: Record<string, number> = { '€': 0x80, '‚': 0x82, '„': 0x84, '…': 0x85, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '™': 0x99 };
/** Text for code page WPC1252 (ESC t 16). UTF-8 would print "OuÃ©draogo"; the printer is not UTF-8. */
export function encode1252(s: string, accents = true): Uint8Array {
  const out: number[] = [];
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (c < 0x80 || (accents && c >= 0xA0 && c <= 0xFF)) out.push(c);
    else if (accents && CP1252[ch] !== undefined) out.push(CP1252[ch]);
    else if (!accents && ch === '·') out.push(0x2D);
    else { const base = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); out.push(base && base.codePointAt(0)! < 0x80 ? base.codePointAt(0)! : 0x3F); }
  }
  return new Uint8Array(out);
}

/** Bytes for the body of a ticket (no INIT, no registration, no cut — the driver owns those). */
/** `accents: false` — for a code page that printed junk for é: letters lose the accent instead. */
export function encodeLayout(ops: LayoutOp[], widthDots: number, qrMethod: C.QrMethod = 'dc3', accents = true): Uint8Array[] {
  const out: Uint8Array[] = [C.CMD_CANCEL_CHINESE, C.cmdSetCodepage(0, 16)];
  for (const op of ops) {
    if (op.k === 'gap') { for (let d = op.dots; d > 0; d -= C.MAX_FEED_DOTS_PER_CMD) out.push(C.cmdFeedDots(Math.min(d, C.MAX_FEED_DOTS_PER_CMD))); continue; }
    if (op.k === 'nv') { out.push(C.cmdPrintNvBmp(op.n, 48)); continue; }
    if (op.k === 'raw') { out.push(op.bytes); continue; }
    if (op.k === 'rule' && op.via === 'underline') {
      // Font B spaces underlined, on a line no taller than the rule needs.
      out.push(new Uint8Array([0x1B, 0x21, 1]), C.cmdSetTextSize(1, 1), C.cmdSetCharSpacing(0), C.cmdSetAlignment(0), C.cmdSetLineSpacing(18), C.cmdSetUnderline(2),
        encode1252(' '.repeat(Math.floor(widthDots / CELL.B.w))), C.cmdSetUnderline(0), C.CMD_LF);
      continue;
    }
    if (op.k === 'rule') {
      const inset = op.inset ?? 0, wb = Math.ceil(widthDots / 8), bits = new Uint8Array(wb * op.dots);
      for (let y = 0; y < op.dots; y++) for (let x = inset; x < widthDots - inset; x++) bits[y * wb + (x >> 3)] |= 0x80 >> (x & 7);
      out.push(C.cmdRaster(wb, op.dots, bits));
      continue;
    }
    if (op.k === 'qr') { out.push(C.cmdSetAlignment(1), C.cmdQr(qrMethod, op.data, op.module), C.CMD_LF); continue; }
    const w = op.w ?? 1, h = op.h ?? 1;
    out.push(
      new Uint8Array([0x1B, 0x21, op.font === 'B' ? 1 : 0]),   // ESC ! — font first: it resets size flags
      C.cmdSetTextSize(w, h),
      C.cmdSetBold(!!op.bold),
      C.cmdSetCharSpacing(op.track ?? 0),
      C.cmdSetAlignment(op.align === 'C' ? 1 : op.align === 'R' ? 2 : 0),
      C.cmdSetLineSpacing(lineHeight(op)),
    );
    // Tab stops are set AT THIS SIZE, so a column lands at the same dot whichever way the firmware
    // scales them (at set time or at HT time).
    if (op.tabs?.length) out.push(C.cmdSetTabPositions(op.tabs));
    out.push(encode1252(op.text.replace(/\t/g, '\x09'), accents), C.CMD_LF);
  }
  out.push(C.cmdSetTextSize(1, 1), C.cmdSetBold(false), C.cmdSetCharSpacing(0), new Uint8Array([0x1B, 0x21, 0]));
  return out;
}

/** Dots of paper an op takes (QR: modules × size + the line after it — `qrModules` supplied by the caller). */
export function opHeight(op: LayoutOp, qrModules: (data: string) => number): number {
  if (op.k === 'gap') return op.dots;
  if (op.k === 'rule') return op.via === 'underline' ? 18 : op.dots;
  if (op.k === 'nv' || op.k === 'raw') return op.heightDots;
  if (op.k === 'qr') return qrModules(op.data) * op.module + 30;
  return lineHeight(op);
}
